import type { ReactNode } from 'react'

import { mensajeDeError } from '../lib/api'
import { Button } from './Button'
import { Card } from './Card'
import { Icon, type IconName } from './Icon'
import { Stack } from './Stack'

/**
 * Los tres estados que todo bloque de datos tiene que saber mostrar: cargando,
 * roto y vacío.
 *
 * Estaban escritos **seis veces**, una por módulo, y no decían lo mismo: dos
 * versiones del error no mostraban título, una ofrecía "Reintentar" en rojo de
 * peligro, dos cargas no llevaban icono y los tres vacíos usaban nombres de prop
 * distintos para el mismo texto. Un panel donde el mismo hecho se ve de seis
 * formas le enseña a la gente que la pantalla es inconsistente, no que pasó algo
 * distinto.
 *
 * Cada módulo sigue teniendo su archivo de estados, pero ahora **compone** estas
 * primitivas: ahí queda lo que es propio de esa pantalla (qué icono, qué
 * `data-testid`, qué palabras) y acá la estructura y el vocabulario visual.
 *
 * Por qué `ErrorBlock` recibe el error crudo y no un mensaje ya armado: la regla
 * del panel es que se muestra **el mensaje de la API**, porque "Tu rol (ops) no
 * tiene acceso a esta sección" se puede accionar y "algo falló" manda a preguntar
 * por Slack. Si la prop fuera un string, cualquier call site podría pasar un
 * genérico sin que nada lo impida. Recibiendo el error, la regla no se puede
 * saltear por descuido — es la única razón por la que este archivo del design
 * system importa de `lib/api`, y solo la función pura que extrae el mensaje.
 */

export interface LoadingBlockProps {
  /** Qué se está trayendo, ej. "el tablero de julio 2026". */
  que: string
  /** Alto mínimo, para que la pantalla no salte al resolver. */
  alto?: 'sm' | 'md' | 'lg'
  /** Borde punteado, cuando el bloque que va a llenarse es de datos que faltan. */
  punteada?: boolean
  testId?: string
}

const ALTO: Record<NonNullable<LoadingBlockProps['alto']>, string> = {
  sm: 'min-h-16',
  md: 'min-h-32',
  lg: 'min-h-64',
}

export function LoadingBlock({
  que,
  alto,
  punteada,
  testId,
}: LoadingBlockProps) {
  return (
    <Card
      padding="md"
      hundida
      punteada={punteada}
      className={alto === undefined ? undefined : ALTO[alto]}
    >
      <p
        role="status"
        data-testid={testId}
        className="inline-flex items-center gap-2 text-meta text-muted-strong"
      >
        <Icon name="reloj" size={13} />
        Cargando {que}…
      </p>
    </Card>
  )
}

export interface ErrorBlockProps {
  /** Qué no se pudo traer, ej. "el portfolio". Arma el título. */
  que: string
  /** El error tal como llegó. El mensaje mostrado es el que mandó la API. */
  error: unknown
  /** Reemplaza el título por completo, ej. "Sin acceso al módulo Admin". */
  titulo?: string
  /** Sin handler no se ofrece reintentar: un 403 no se arregla reintentando. */
  onRetry?: () => void
  reintentando?: boolean
  /** Qué hacer al respecto, cuando hay algo que decir además del error. */
  nota?: ReactNode
  /** `data-testid` del párrafo del mensaje. */
  testId?: string
}

export function ErrorBlock({
  que,
  error,
  titulo,
  onRetry,
  reintentando,
  nota,
  testId,
}: ErrorBlockProps) {
  const encabezado = titulo ?? `No pudimos cargar ${que}`

  return (
    <Card as="section" ariaLabel={encabezado} padding="md" severidad="critical">
      <Stack gap="sm" align="start">
        {/* h2 y no h1: el h1 de la página lo pone el header del shell. */}
        <h2 className="inline-flex items-start gap-2 text-titulo font-medium text-text">
          <Icon name="critico" size={14} className="mt-px text-danger-text" />
          {encabezado}
        </h2>
        {/* El mensaje del backend, textual: es el que dice qué pasó de verdad. */}
        <p
          role="alert"
          data-testid={testId}
          className="max-w-prosa text-meta text-muted-strong"
        >
          {mensajeDeError(error)}
        </p>
        {nota}
        {onRetry !== undefined ? (
          <Button
            variante="secundario"
            icono="sincronizar"
            onClick={onRetry}
            cargando={reintentando}
          >
            Reintentar
          </Button>
        ) : null}
      </Stack>
    </Card>
  )
}

export interface EmptyBlockProps {
  /** Qué no hay, en una oración. Nunca "sin resultados" pelado. */
  mensaje: string
  /** Por qué está vacío, o qué hacer para que deje de estarlo. */
  detalle?: string
  /** Borde punteado: el vacío es un dato que falta, no una buena noticia. */
  punteada?: boolean
  /** `null` para no mostrar ninguno. */
  icono?: IconName | null
  testId?: string
  /** La acción que llena el vacío. */
  children?: ReactNode
}

export function EmptyBlock({
  mensaje,
  detalle,
  punteada,
  icono = 'sin-dato',
  testId,
  children,
}: EmptyBlockProps) {
  return (
    <Card padding="md" hundida punteada={punteada}>
      <Stack gap="xs" align="start">
        <p
          data-testid={testId}
          className="inline-flex items-center gap-2 text-meta font-medium text-text"
        >
          {icono === null ? null : <Icon name={icono} size={13} />}
          {mensaje}
        </p>
        {detalle !== undefined ? (
          <p className="text-micro text-muted-strong">{detalle}</p>
        ) : null}
        {children}
      </Stack>
    </Card>
  )
}

export interface MissingCellProps {
  /** Qué falta, en singular: "fórmula", "dueño". Va en el aria del botón. */
  que: string
  /** Ausente = no hay acción de carga para este campo. */
  onCargar?: () => void
  textoAccion?: string
}

/**
 * "Sin dato → cargalo acá" del tamaño de una celda.
 *
 * Es la regla 2 del panel llevada a la tabla: un guion no distingue "no aplica"
 * de "nadie lo cargó". `EmptyMetricState` dice lo mismo pero mide una tarjeta y
 * no entra en una fila de 32px, así que hacían falta las dos.
 */
export function MissingCell({
  que,
  onCargar,
  textoAccion = 'Cargalo acá',
}: MissingCellProps) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-micro text-muted-strong">
      <Icon name="sin-dato" size={11} />
      <span>Sin {que}</span>
      {onCargar !== undefined ? (
        <Button variante="chico" onClick={onCargar}>
          {textoAccion}
          <span className="sr-only"> — {que}</span>
        </Button>
      ) : null}
    </span>
  )
}
